import {
  Button,
  SelectInput,
  SelectInputItem,
  Switch,
  TextField,
  useAlert,
} from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CountryLangViewModel } from '@/shared/api/generated';

import { useAvailableLanguages } from '../../../hooks/useAvailableLanguages';
import { useSaveCountryLang } from '../../../hooks/useSaveCountryLang';
import { type CountryLangFormValues, countryLangFormSchema } from '../../../types/countryLangForm';

export interface CountryLangFormModalProps {
  readonly open: boolean;
  readonly countryId: number;
  /** Idioma em edição; `null` abre o modal em modo de criação. */
  readonly lang: CountryLangViewModel | null;
  readonly onOpenChange: (open: boolean) => void;
}

const DEFAULT_VALUES: CountryLangFormValues = { cultureInfo: '', name: '', active: true };

/**
 * Modal de criação/edição de idioma do país (Radix Dialog + React Hook Form + Zod).
 *
 * Um único formulário cobre criar e editar (pré-preenchido a partir da linha). O
 * Idioma é escolhido num `SelectInput` alimentado por `useAvailableLanguages`
 * (armazena `cultureInfo`). Persiste via `useSaveCountryLang` e mapeia o conflito
 * **409** (idioma já cadastrado) para erro no campo Idioma. Usa `modal={false}` +
 * backdrop manual + `onInteractOutside` porque o dropdown do `SelectInput` é
 * portado ao body (ver reference_ads_autocomplete_selecteditem). Fiel aos frames
 * *Idiomas — Modal: Adicionar/Editar* de `design/location.pen`.
 */
export function CountryLangFormModal({
  open,
  countryId,
  lang,
  onOpenChange,
}: CountryLangFormModalProps) {
  const { t } = useTranslation('location');
  const { alert } = useAlert();
  const { save, isPending } = useSaveCountryLang(countryId);
  const { options, isLoading: isLoadingLanguages } = useAvailableLanguages(countryId);

  const isEdit = lang?.id != null;

  // Em edição, garante que a cultura atual apareça no select mesmo que o endpoint
  // de idiomas disponíveis não a inclua (available = ainda não cadastrados).
  const languageOptions = useMemo(() => {
    if (lang?.cultureInfo && !options.some(o => o.key === lang.cultureInfo)) {
      return [
        { key: lang.cultureInfo, value: lang.cultureDisplayName ?? lang.cultureInfo },
        ...options,
      ];
    }
    return options;
  }, [options, lang]);

  // Sem idiomas disponíveis (e já carregado): desabilita o seletor e o salvar.
  const noLanguages = !isLoadingLanguages || languageOptions.length === 0;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CountryLangFormValues>({
    resolver: zodResolver(countryLangFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!open) return;
    reset(
      lang
        ? {
            cultureInfo: lang.cultureInfo ?? '',
            name: lang.name ?? '',
            active: lang.active ?? true,
          }
        : DEFAULT_VALUES,
    );
  }, [open, lang, reset]);

  const onSubmit = handleSubmit(values => {
    save(values, lang?.id ?? undefined, {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t(
            isEdit ? 'countryLangs.toast.updateSuccess' : 'countryLangs.toast.createSuccess',
          ),
        });
        onOpenChange(false);
      },
      onError: () => alert({ alertType: 'warning', title: t('countryLangs.toast.saveError') }),
    });
  });

  const languageHelper = errors.cultureInfo
    ? t('countryLangs.form.errors.languageRequired')
    : undefined;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Dialog.Portal>
        {/* Backdrop manual: `modal={false}` mantém o dropdown do SelectInput (portado
            ao body) clicável. Ver reference_ads_autocomplete_selecteditem. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />
        <Dialog.Content
          aria-describedby={undefined}
          onInteractOutside={e => e.preventDefault()}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex w-full max-w-[32rem] flex-col gap-xxs rounded-lg bg-ctx-layout-surface p-xxs shadow-level4 focus:outline-none"
        >
          <Dialog.Title className="text-ctx-content-title ts-heading-xs">
            {t(isEdit ? 'countryLangs.form.editTitle' : 'countryLangs.form.createTitle')}
          </Dialog.Title>

          <form className="flex flex-col gap-mini" onSubmit={onSubmit} noValidate>
            <Controller
              control={control}
              name="cultureInfo"
              render={({ field }) => (
                <div className="flex flex-col gap-quark">
                  {/* ADS SelectInput nem sempre reflete o `disabled` no trigger; o wrapper
                      garante o estado desabilitado (sem clique, esmaecido) quando não há
                      idiomas disponíveis. */}
                  <div
                    className={noLanguages ? 'pointer-events-none opacity-medium' : undefined}
                    aria-disabled={noLanguages}
                  >
                    <SelectInput
                      label={`${t('countryLangs.form.language')}*`}
                      placeholder={t('countryLangs.form.languagePlaceholder')}
                      value={field.value || ''}
                      onValueChange={v => field.onChange(v)}
                      size="md"
                      fullWidth
                      disabled={noLanguages}
                      hasError={Boolean(errors.cultureInfo)}
                      {...(languageHelper ? { helper: languageHelper } : {})}
                    >
                      {languageOptions.map(option => (
                        <SelectInputItem
                          key={option.key}
                          value={option.key ?? ''}
                          custom={{ root: 'text-nowrap' }}
                        >
                          {option.value ?? ''}
                        </SelectInputItem>
                      ))}
                    </SelectInput>
                  </div>
                  {noLanguages ? (
                    <p className="text-ctx-content-placeholder ts-caption-xxs">
                      {t('countryLangs.form.noLanguages')}
                    </p>
                  ) : null}
                </div>
              )}
            />

            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <TextField
                  label={`${t('countryLangs.form.name')}*`}
                  placeholder={t('countryLangs.form.namePlaceholder')}
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  size="md"
                  fullWidth
                  hasError={Boolean(errors.name)}
                  {...(errors.name ? { helper: t('countryLangs.form.errors.nameRequired') } : {})}
                />
              )}
            />

            <Controller
              control={control}
              name="active"
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  size="md"
                  label={t('countryLangs.form.active')}
                  onCheckedChange={checked => field.onChange(checked)}
                />
              )}
            />

            <div className="mt-nano flex justify-end gap-nano">
              <Dialog.Close asChild>
                <Button hierarchy="secondary" size="md" type="button">
                  {t('countryLangs.form.cancel')}
                </Button>
              </Dialog.Close>
              <Button
                hierarchy="primary"
                size="md"
                type="submit"
                isLoading={isPending}
                disabled={isPending || noLanguages}
              >
                {t('countryLangs.form.save')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
